/**
 * Media API - Core CRUD Operations
 *
 * List, Get, Create, Update, Delete media endpoints.
 */

import { z } from 'zod'
import {
  MediaTypeSchema,
  MediaVisibilitySchema,
  FaceDetectionStatusSchema,
  ProcessingStatusSchema,
  MediaUploaderSchema,
  UploadContextSchema
} from './common'
import { MAX_AUDIO_DURATION_SECONDS } from './upload'

/**
 * Length of an audio file, in whole seconds, as the CLIENT measured it after
 * the pick (`MediaCreateDurationSchema`). Optional on every create path: a file
 * the device cannot measure still uploads, and its row simply carries no
 * duration (SCH-2130, R4a).
 *
 * The ceiling makes the 10-minute limit executable rather than declarative —
 * but it is a check on a STATED value. Nothing on the server reads the file's
 * length, so a caller can under-state it; the 100MB size cap is the hard limit.
 */
const MediaCreateDurationSchema = z.number().int().positive().max(MAX_AUDIO_DURATION_SECONDS).optional()

// ============================================================================
// GET /v1/media (List)
// ============================================================================

/**
 * GET /v1/media.
 *
 * The wire shape is unchanged, but the DEFAULT browse (no `group_id`) is now
 * membership-derived (plan 006): the response is the union of media in
 * collections the viewer can see (explicit member / PUBLIC / creator /
 * source group / source lead-section) plus the viewer's own uploads — NOT the
 * full school. `uploaded_by_user_id` is honored unrestricted only for self or
 * a school-admin; for any other target it is intersected with the viewer's
 * union (KTD-1b). No authenticated viewer context → empty result. The
 * `group_id` path retains its existing collection-anchored scoping.
 */
export const ListMediaRequestSchema = z.object({
  limit: z.coerce.number().positive().max(100).optional(),
  offset: z.coerce.number().min(0).optional(),
  school_id: z.string().optional(),
  uploaded_by_user_id: z.string().optional(),
  media_type: MediaTypeSchema.optional(),
  visibility: MediaVisibilitySchema.optional(),
  face_detection_status: FaceDetectionStatusSchema.optional(),
  person_id: z.string().optional(),
  group_id: z.string().optional(),
  uploaded_after: z.coerce.date().optional(),
  uploaded_before: z.coerce.date().optional(),
  captured_after: z.coerce.date().optional(),
  captured_before: z.coerce.date().optional(),
  search_query: z.string().optional(),
  order_by: z.enum(['capturedAt', 'createdAt', 'updatedAt', 'fileSize']).optional(),
  order_direction: z.enum(['asc', 'desc']).optional(),
  include_deleted: z.coerce.boolean().optional()
})

export type ListMediaRequest = z.infer<typeof ListMediaRequestSchema>

export const MediaItemSchema = z.object({
  id: z.string(),
  schoolId: z.string().nullable(),
  mediaType: MediaTypeSchema,
  s3Key: z.string(),
  s3Bucket: z.string(),
  s3Region: z.string(),
  cloudinaryUrl: z.string().nullable(),
  cloudinaryThumbnailUrl: z.string().nullable(),
  cloudinaryId: z.string().nullable(),
  // Mux video provider (new uploads). Both null for image rows and legacy Cloudinary video rows.
  muxAssetId: z.string().nullable().optional(),
  muxPlaybackId: z.string().nullable().optional(),
  originalFilename: z.string(),
  mimeType: z.string(),
  fileSize: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  duration: z.number().nullable(),
  capturedAt: z.coerce.date().nullable(),
  uploadedBy: MediaUploaderSchema.nullable(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  // User-provided caption (separate from description)
  caption: z.string().nullable().optional(),
  // Convenience alias: url maps to cloudinaryUrl for backwards compatibility
  url: z.string().nullable().optional(),
  // Convenience alias: thumbnailUrl maps to cloudinaryThumbnailUrl
  thumbnailUrl: z.string().nullable().optional(),
  // Convenience alias: type maps to mediaType (legacy components use this)
  type: z.string().nullable().optional(),
  visibility: MediaVisibilitySchema,
  faceDetectionStatus: FaceDetectionStatusSchema,
  faceDetectionCompletedAt: z.coerce.date().nullable(),
  faceDetectionError: z.string().nullable(),
  processingStatus: ProcessingStatusSchema,
  processingError: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable()
})

export type MediaItem = z.infer<typeof MediaItemSchema>

export const ListMediaResponseSchema = z.object({
  message: z.string(),
  data: z.array(MediaItemSchema),
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number()
  })
})

export type ListMediaResponse = z.infer<typeof ListMediaResponseSchema>

// ============================================================================
// GET /v1/media/:id (Get Single)
// ============================================================================

/**
 * GET /v1/media/:id.
 *
 * The single-media read is gated by the same membership contract as the
 * default browse (plan 006 U3): the request succeeds only for the uploader or
 * a viewer who shares at least one visible collection with the media (or it is
 * PUBLIC / creator-owned). Unauthorized and cross-school reads return the same
 * not-found error as a nonexistent id (no existence leak). Wire shape
 * unchanged.
 */
export const GetMediaRequestSchema = z.object({
  id: z.string().min(1),
  include_uploader: z.coerce.boolean().optional(),
  include_collections: z.coerce.boolean().optional(),
  include_faces: z.coerce.boolean().optional()
})

export type GetMediaRequest = z.infer<typeof GetMediaRequestSchema>

export const GetMediaResponseSchema = z.object({
  message: z.string(),
  data: MediaItemSchema
})

export type GetMediaResponse = z.infer<typeof GetMediaResponseSchema>

// ============================================================================
// POST /v1/media (Create)
// ============================================================================

export const CreateMediaRequestSchema = z.object({
  mediaId: z.string().min(1),
  s3Key: z.string().min(1),
  originalFilename: z.string().min(1).max(500),
  mimeType: z.string().min(1),
  fileSize: z.number().positive(),
  title: z.string().max(500).optional(),
  description: z.string().max(5000).optional(),
  tags: z.array(z.string()).optional(),
  visibility: MediaVisibilitySchema.optional(),
  duration: MediaCreateDurationSchema
})

export type CreateMediaRequest = z.infer<typeof CreateMediaRequestSchema>

export const CreateMediaResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    id: z.string(),
    s3Key: z.string(),
    originalFilename: z.string(),
    mimeType: z.string(),
    fileSize: z.number(),
    mediaType: MediaTypeSchema,
    processingStatus: ProcessingStatusSchema,
    cloudinaryUrl: z.string().nullable(),
    cloudinaryThumbnailUrl: z.string().nullable(),
    uploader: z.object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string()
    })
  })
})

export type CreateMediaResponse = z.infer<typeof CreateMediaResponseSchema>

// ============================================================================
// POST /v1/media/batch (Create Batch)
// ============================================================================

export const BatchMediaItemSchema = z.object({
  mediaId: z.string().min(1),
  s3Key: z.string().min(1),
  originalFilename: z.string().min(1).max(500),
  mimeType: z.string().min(1),
  fileSize: z.number().positive(),
  title: z.string().max(500).optional(),
  description: z.string().max(5000).optional(),
  tags: z.array(z.string()).optional(),
  // Capture timestamp + GPS read from the picker asset's EXIF at pick time
  // (SCH-140). WRITE-ONLY: persisted on the Media row but NEVER serialized into
  // any client-facing read response (FERPA; future location filtering is
  // server-side). Do NOT add these to MediaItemSchema / MediaResponse.
  capturedAt: z.coerce.date().optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  duration: MediaCreateDurationSchema
})

export type BatchMediaItem = z.infer<typeof BatchMediaItemSchema>

export const CreateBatchMediaRequestSchema = z.object({
  media: z.array(BatchMediaItemSchema).min(1).max(50),
  context: UploadContextSchema.optional()
})

export type CreateBatchMediaRequest = z.infer<typeof CreateBatchMediaRequestSchema>

export const BatchMediaResponseItemSchema = z.object({
  id: z.string(),
  s3Key: z.string(),
  originalFilename: z.string(),
  mimeType: z.string(),
  fileSize: z.number(),
  mediaType: MediaTypeSchema,
  processingStatus: ProcessingStatusSchema,
  cloudinaryUrl: z.string().nullable(),
  uploader: z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string()
  })
})

export type BatchMediaResponseItem = z.infer<typeof BatchMediaResponseItemSchema>

export const CreateBatchMediaResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    media: z.array(BatchMediaResponseItemSchema),
    totalCreated: z.number(),
    failed: z
      .array(
        z.object({
          tempKey: z.string(),
          error: z.string()
        })
      )
      .optional(),
    collectionId: z.string().optional()
  })
})

export type CreateBatchMediaResponse = z.infer<typeof CreateBatchMediaResponseSchema>

// ============================================================================
// PATCH /v1/media/:id (Update)
// ============================================================================

export const UpdateMediaRequestSchema = z.object({
  // id is typically in URL path, make optional for body
  id: z.string().min(1).optional(),
  title: z.string().max(500).optional(),
  description: z.string().max(5000).optional(),
  visibility: MediaVisibilitySchema.optional(),
  capturedAt: z.coerce.date().optional()
})

export type UpdateMediaRequest = z.infer<typeof UpdateMediaRequestSchema>

export const UpdateMediaResponseSchema = z.object({
  message: z.string(),
  data: MediaItemSchema
})

export type UpdateMediaResponse = z.infer<typeof UpdateMediaResponseSchema>

// ============================================================================
// DELETE /v1/media/:id (Delete)
// ============================================================================

export const DeleteMediaRequestSchema = z.object({
  id: z.string().min(1)
})

export type DeleteMediaRequest = z.infer<typeof DeleteMediaRequestSchema>

export const DeleteMediaResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    success: z.boolean()
  })
})

export type DeleteMediaResponse = z.infer<typeof DeleteMediaResponseSchema>
